// Neuer Lead von Hand: Firma, Kontakt, Telefon, E-Mail, Status, Bearbeiter. Vor dem Speichern sucht der Dialog nach
// Dubletten (Telefon, E-Mail, Firmenname) und zeigt „Gibt es schon: … öffnen“. Gespeichert wird in Close (Schlüssel des
// handelnden Nutzers), danach öffnet sich der neue Lead.
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ApiFehler, fehlerText } from "../../api";
import { leadNeuApi, type DublettenTreffer, type NeuerLeadEingabe } from "../../api/leadsNeu";
import { useAuth } from "../../auth/AuthContext";
import { useMeta } from "../../daten/meta";
import { Auswahl } from "../Auswahl";
import { Dialog } from "../Dialog";
import { Icon } from "../Icon";
import { useMeldung } from "../Meldungen";
import "./smartview.css";

const GRUND: Record<string, string> = { telefon: "gleiche Telefonnummer", email: "gleiche E-Mail", name: "gleicher Firmenname" };

export function NeuerLeadDialog({ offen, onSchliessen, vorbelegung }: { offen: boolean; onSchliessen: () => void; vorbelegung?: Partial<NeuerLeadEingabe> }) {
  const navigate = useNavigate();
  const meldung = useMeldung();
  const { meta, nutzerListe } = useMeta();
  const { nutzer } = useAuth();
  const [firma, setFirma] = useState("");
  const [kontakt, setKontakt] = useState("");
  const [telefon, setTelefon] = useState("");
  const [email, setEmail] = useState("");
  const [statusId, setStatusId] = useState<string | null>(null);
  const [bearbeiter, setBearbeiter] = useState<string | null>(null);
  const [dubletten, setDubletten] = useState<DublettenTreffer[]>([]);
  const [trotzdem, setTrotzdem] = useState(false);
  const [speichert, setSpeichert] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const bearbeiterFeld = meta?.custom_fields.lead.find((f) => f.type === "user" && f.name.trim().toLowerCase() === "bearbeiter");

  useEffect(() => {
    if (!offen) return;
    setFirma(vorbelegung?.firma ?? "");
    setKontakt(vorbelegung?.kontakt_name ?? "");
    setTelefon(vorbelegung?.telefon ?? "");
    setEmail(vorbelegung?.email ?? "");
    setStatusId(vorbelegung?.status_id ?? null);
    setBearbeiter(vorbelegung?.bearbeiter_id ?? nutzer?.close_user_id ?? null);
    setDubletten([]);
    setTrotzdem(false);
    setFehler(null);
    setSpeichert(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offen]);

  // Dubletten-Suche, kurz verzögert, sobald Firma, Telefon oder E-Mail eingegeben sind
  useEffect(() => {
    if (!offen) return;
    const f = firma.trim();
    const t = telefon.trim();
    const m = email.trim();
    if (f.length < 3 && t.length < 5 && !m.includes("@")) {
      setDubletten([]);
      return;
    }
    let aktuell = true;
    const zeit = window.setTimeout(() => {
      leadNeuApi
        .dubletten({ firma: f || undefined, telefon: t || undefined, email: m || undefined })
        .then((r) => aktuell && setDubletten(r.data))
        .catch(() => aktuell && setDubletten([]));
    }, 350);
    return () => {
      aktuell = false;
      window.clearTimeout(zeit);
    };
  }, [offen, firma, telefon, email]);

  const hart = useMemo(() => dubletten.filter((d) => d.gruende.some((g) => g.art !== "name")), [dubletten]);
  const nurName = useMemo(() => dubletten.filter((d) => d.gruende.every((g) => g.art === "name")), [dubletten]);
  useEffect(() => {
    if (!hart.length) setTrotzdem(false);
  }, [hart.length]);

  const oeffnenLead = (id: string) => {
    onSchliessen();
    navigate(`/lead/${encodeURIComponent(id)}`);
  };

  const speichern = async () => {
    if (!firma.trim()) {
      setFehler("Bitte den Firmennamen eintragen.");
      return;
    }
    if (hart.length && !trotzdem) {
      setFehler("Es gibt schon einen Lead mit dieser Nummer oder Mail. Bitte öffnen oder unten bestätigen, dass trotzdem ein neuer angelegt wird.");
      return;
    }
    setSpeichert(true);
    setFehler(null);
    try {
      const r = await leadNeuApi.anlegen({
        firma: firma.trim(),
        kontakt_name: kontakt.trim() || null,
        telefon: telefon.trim() || null,
        email: email.trim() || null,
        status_id: statusId,
        bearbeiter_id: bearbeiterFeld ? bearbeiter : null,
        dubletten_ignorieren: trotzdem,
      });
      meldung.ok(`Lead „${r.lead.display_name ?? r.lead.name ?? firma.trim()}“ angelegt.`);
      onSchliessen();
      navigate(`/lead/${encodeURIComponent(r.lead.id)}`);
    } catch (e) {
      if (e instanceof ApiFehler && e.code === "dublette") {
        setDubletten((e.details.treffer as DublettenTreffer[]) ?? []);
        setFehler("Es gibt schon einen Lead mit dieser Nummer oder Mail.");
      } else setFehler(fehlerText(e));
      setSpeichert(false);
    }
  };

  const statusOptionen = (meta?.lead_statuses ?? []).map((s) => ({ wert: s.id, label: s.label, farbe: s.color }));
  const nutzerOptionen = nutzerListe.filter((u) => u.close_user_id && u.is_active).map((u) => ({ wert: u.close_user_id as string, label: u.name }));

  return (
    <Dialog
      titel="Neuer Lead"
      offen={offen}
      onSchliessen={onSchliessen}
      breite={560}
      fuss={
        <>
          <button type="button" className="knopf" onClick={onSchliessen}>
            Abbrechen
          </button>
          <button type="submit" form="neuer-lead-form" className="knopf knopf--primaer" disabled={speichert || !firma.trim()}>
            {speichert ? "Wird angelegt …" : hart.length ? "Trotzdem anlegen" : "Lead anlegen"}
          </button>
        </>
      }
    >
      <form
        id="neuer-lead-form"
        className="svd"
        onSubmit={(e) => {
          e.preventDefault();
          void speichern();
        }}
      >
        <label className="feld">
          <span className="feld__label">Firma *</span>
          <input className="eingabe" value={firma} onChange={(e) => setFirma(e.target.value)} maxLength={500} autoComplete="off" />
        </label>
        <div className="svd__zeile">
          <label className="feld">
            <span className="feld__label">Kontakt</span>
            <input className="eingabe" value={kontakt} onChange={(e) => setKontakt(e.target.value)} maxLength={500} autoComplete="off" />
          </label>
          <label className="feld">
            <span className="feld__label">Telefon</span>
            <input className="eingabe" type="tel" value={telefon} onChange={(e) => setTelefon(e.target.value)} maxLength={64} autoComplete="off" />
          </label>
        </div>
        <label className="feld">
          <span className="feld__label">E-Mail</span>
          <input className="eingabe" type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={320} autoComplete="off" />
        </label>
        <div className="svd__zeile">
          <div className="feld">
            <span className="feld__label">Status</span>
            <Auswahl optionen={statusOptionen} wert={statusId} leerErlaubt platzhalter="Standard von Close" onWahl={(w) => setStatusId((w as string) || null)} />
          </div>
          {bearbeiterFeld ? (
            <div className="feld">
              <span className="feld__label">Bearbeiter</span>
              <Auswahl optionen={nutzerOptionen} wert={bearbeiter} leerErlaubt platzhalter="Niemand" onWahl={(w) => setBearbeiter((w as string) || null)} />
            </div>
          ) : null}
        </div>

        {hart.length ? (
          <div className="hinweis hinweis--warnung" role="alert">
            <Icon name="warnung" groesse={15} />
            <div className="svd__dublette">
              {hart.map((d) => (
                <div key={d.lead_id} className="svd__dublette-zeile">
                  <span>
                    Gibt es schon: <strong>{d.name}</strong>
                    {d.status_label ? ` (${d.status_label})` : ""}, {d.gruende.map((g) => GRUND[g.art]).join(" und ")}
                  </span>
                  <button type="button" className="textknopf" onClick={() => oeffnenLead(d.lead_id)}>
                    öffnen
                  </button>
                </div>
              ))}
              <label className="svd__haken">
                <input type="checkbox" checked={trotzdem} onChange={(e) => setTrotzdem(e.target.checked)} />
                <span>Trotzdem neu anlegen (die Automatik führt doppelte Leads nach wenigen Minuten wieder zusammen)</span>
              </label>
            </div>
          </div>
        ) : null}
        {nurName.length ? (
          <div className="hinweis hinweis--info">
            <Icon name="info" groesse={15} />
            <div className="svd__dublette">
              {nurName.map((d) => (
                <div key={d.lead_id} className="svd__dublette-zeile">
                  <span>
                    Gleicher Firmenname: <strong>{d.name}</strong>
                    {d.status_label ? ` (${d.status_label})` : ""}
                  </span>
                  <button type="button" className="textknopf" onClick={() => oeffnenLead(d.lead_id)}>
                    öffnen
                  </button>
                </div>
              ))}
            </div>
          </div>
        ) : null}
        {fehler ? (
          <div className="hinweis hinweis--fehler" role="alert">
            <Icon name="warnung" groesse={15} />
            <span>{fehler}</span>
          </div>
        ) : null}
      </form>
    </Dialog>
  );
}
